/**
 * Enums of the invoicing engine (SWOOP-421). Values are taken verbatim from the design
 * (specs/invoicing-engine/data-model.md, revision 13); the section that defines each one is named.
 */

/** The module a charge comes from (§3.3, §3.9). `LEASING` and `RECHARGE` are the MVP providers. */
export const BILLING_SOURCE = {
  LEASING: 'LEASING',
  MANUAL: 'MANUAL',
  RECHARGE: 'RECHARGE',
  RENTAL: 'RENTAL',
  TRANSPORT: 'TRANSPORT',
  WORKSHOP: 'WORKSHOP',
} as const;

export type BillingSource = (typeof BILLING_SOURCE)[keyof typeof BILLING_SOURCE];

/** The kind of charge within a billing source (§3.3). Each maps to a document category in the registry. */
export const CHARGE_SOURCE_TYPE = {
  ADMIN_FEE: 'ADMIN_FEE',
  CONTRACT_FEE: 'CONTRACT_FEE',
  DAMAGE_RECHARGE: 'DAMAGE_RECHARGE',
  FINE_RECHARGE: 'FINE_RECHARGE',
  LEASE_RENT: 'LEASE_RENT',
  MANUAL_ADJUSTMENT: 'MANUAL_ADJUSTMENT',
} as const;

export type ChargeSourceType = (typeof CHARGE_SOURCE_TYPE)[keyof typeof CHARGE_SOURCE_TYPE];

/** `CANCELLED` is terminal; a cancelled charge's key stays taken (§3.3). */
export const CHARGE_STATE = {
  CANCELLED: 'CANCELLED',
  INVOICED: 'INVOICED',
  READY: 'READY',
  RESERVED: 'RESERVED',
} as const;

export type ChargeState = (typeof CHARGE_STATE)[keyof typeof CHARGE_STATE];

/** Legal state only; there is no `PENDING` (§3.5). */
export const LEGAL_STATUS = {
  DRAFT: 'DRAFT',
  ISSUED: 'ISSUED',
  VOID: 'VOID',
} as const;

export type LegalStatus = (typeof LEGAL_STATUS)[keyof typeof LEGAL_STATUS];

/** Legal type of a document; the business kind is `DocumentCategory` (§3.5). */
export const INVOICE_TYPE = {
  CREDIT_NOTE: 'CREDIT_NOTE',
  INVOICE: 'INVOICE',
} as const;

export type InvoiceType = (typeof INVOICE_TYPE)[keyof typeof INVOICE_TYPE];

/** Business kind of document (§3.5). More values arrive with their provider. */
export const DOCUMENT_CATEGORY = {
  ADJUSTMENT: 'ADJUSTMENT',
  DAMAGE: 'DAMAGE',
  GENERAL: 'GENERAL',
  LEASE: 'LEASE',
} as const;

export type DocumentCategory = (typeof DOCUMENT_CATEGORY)[keyof typeof DOCUMENT_CATEGORY];

/** The two-letter code printed in the invoice number (§3.6). */
export const DOCUMENT_CODE = {
  AJ: 'AJ',
  BC: 'BC',
  CR: 'CR',
  DM: 'DM',
} as const;

export type DocumentCode = (typeof DOCUMENT_CODE)[keyof typeof DOCUMENT_CODE];

export const PAYMENT_STATUS = {
  PAID: 'PAID',
  PARTIAL: 'PARTIAL',
  UNKNOWN: 'UNKNOWN',
  UNPAID: 'UNPAID',
} as const;

export type PaymentStatus = (typeof PAYMENT_STATUS)[keyof typeof PAYMENT_STATUS];

/** Email delivery of the issued PDF (§3.5). Prefixed because `DELIVERY_STATUS` already names the vehicle delivery. */
export const INVOICE_DELIVERY_STATUS = {
  DELIVERED: 'DELIVERED',
  FAILED: 'FAILED',
  NOT_QUEUED: 'NOT_QUEUED',
  QUEUED: 'QUEUED',
} as const;

export type InvoiceDeliveryStatus = (typeof INVOICE_DELIVERY_STATUS)[keyof typeof INVOICE_DELIVERY_STATUS];

export const ERP_SYNC_STATUS = {
  FAILED: 'FAILED',
  NOT_CONFIGURED: 'NOT_CONFIGURED',
  PENDING: 'PENDING',
  REVIEW: 'REVIEW',
  SYNCED: 'SYNCED',
} as const;

export type ErpSyncStatus = (typeof ERP_SYNC_STATUS)[keyof typeof ERP_SYNC_STATUS];

/** Who issued the legal document (§3.5). A registered ERP connector key is also a valid value. */
export const SOURCE_PLATFORM = {
  DYNAMICS_365: 'DYNAMICS_365',
  HETIVO: 'HETIVO',
  IMPORT: 'IMPORT',
  ODOO: 'ODOO',
} as const;

export type SourcePlatform = (typeof SOURCE_PLATFORM)[keyof typeof SOURCE_PLATFORM];

export const RUN_MODE = {
  MONTHLY: 'MONTHLY',
  SOLO: 'SOLO',
  SYNC: 'SYNC',
} as const;

export type RunMode = (typeof RUN_MODE)[keyof typeof RUN_MODE];

/** Status of an invoice request (§3.4). The design names no values for it; these are the ones its text uses. */
export const RUN_STATUS = {
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  QUEUED: 'QUEUED',
  RUNNING: 'RUNNING',
} as const;

export type RunStatus = (typeof RUN_STATUS)[keyof typeof RUN_STATUS];

export const GROUP_STATUS = {
  AUTO_ISSUED: 'AUTO_ISSUED',
  DRAFTED: 'DRAFTED',
  FAILED: 'FAILED',
  ISSUED: 'ISSUED',
  PENDING: 'PENDING',
  REVIEW: 'REVIEW',
} as const;

export type GroupStatus = (typeof GROUP_STATUS)[keyof typeof GROUP_STATUS];

export const JOB_TYPE = {
  ACCOUNTING_SWEEP: 'ACCOUNTING_SWEEP',
  DELIVERY: 'DELIVERY',
  ISSUE_BATCH_SWEEP: 'ISSUE_BATCH_SWEEP',
  PAYMENT_PULL: 'PAYMENT_PULL',
  REMINDER: 'REMINDER',
  RUN_DISPATCH: 'RUN_DISPATCH',
  SCHEDULED_EXPORT: 'SCHEDULED_EXPORT',
  SOURCE_SYNC: 'SOURCE_SYNC',
} as const;

export type JobType = (typeof JOB_TYPE)[keyof typeof JOB_TYPE];

export const JOB_STATUS = {
  FAILED: 'FAILED',
  PARTIAL: 'PARTIAL',
  RUNNING: 'RUNNING',
  SUCCEEDED: 'SUCCEEDED',
} as const;

export type JobStatus = (typeof JOB_STATUS)[keyof typeof JOB_STATUS];

/** `STOPPED` is a systemic stop; `FAILED` only when the worker could not start (§3.20). */
export const ISSUE_BATCH_STATUS = {
  COMPLETED: 'COMPLETED',
  COMPLETED_WITH_FAILURES: 'COMPLETED_WITH_FAILURES',
  FAILED: 'FAILED',
  QUEUED: 'QUEUED',
  RUNNING: 'RUNNING',
  STOPPED: 'STOPPED',
} as const;

export type IssueBatchStatus = (typeof ISSUE_BATCH_STATUS)[keyof typeof ISSUE_BATCH_STATUS];

export const ISSUE_BATCH_ITEM_STATUS = {
  FAILED: 'FAILED',
  ISSUED: 'ISSUED',
  PENDING: 'PENDING',
  SKIPPED: 'SKIPPED',
} as const;

export type IssueBatchItemStatus = (typeof ISSUE_BATCH_ITEM_STATUS)[keyof typeof ISSUE_BATCH_ITEM_STATUS];

/** `MANUAL` is explicit invoice IDs; `RUN` is the drafts of a run (§3.20). */
export const ISSUE_BATCH_SOURCE = {
  MANUAL: 'MANUAL',
  RUN: 'RUN',
} as const;

export type IssueBatchSource = (typeof ISSUE_BATCH_SOURCE)[keyof typeof ISSUE_BATCH_SOURCE];

/** `SUMMARY` only on imported invoices, which have no charges (§3.5). */
export const LINE_KIND = {
  CHARGE: 'CHARGE',
  CREDIT: 'CREDIT',
  SUMMARY: 'SUMMARY',
} as const;

export type LineKind = (typeof LINE_KIND)[keyof typeof LINE_KIND];

export const CREDIT_REASON = {
  DAMAGE_DISPUTE: 'DAMAGE_DISPUTE',
  DUPLICATE_CHARGE: 'DUPLICATE_CHARGE',
  EARLY_TERMINATION: 'EARLY_TERMINATION',
  FINE_CANCELLED: 'FINE_CANCELLED',
  GOODWILL: 'GOODWILL',
  OTHER: 'OTHER',
  PRICE_ERROR: 'PRICE_ERROR',
  VEHICLE_SUBSTITUTION: 'VEHICLE_SUBSTITUTION',
} as const;

export type CreditReason = (typeof CREDIT_REASON)[keyof typeof CREDIT_REASON];

/** Which sink receives the accounting document (§3.1). `FILE_EXPORT` is the only stored value in the MVP. */
export const INTEGRATION_MODE = {
  ERP_ISSUES: 'ERP_ISSUES',
  ERP_PUSH: 'ERP_PUSH',
  FILE_EXPORT: 'FILE_EXPORT',
} as const;

export type IntegrationMode = (typeof INTEGRATION_MODE)[keyof typeof INTEGRATION_MODE];

export const AUTOMATION_MODE = {
  ASSISTED: 'ASSISTED',
  AUTOMATIC: 'AUTOMATIC',
  MANUAL: 'MANUAL',
} as const;

export type AutomationMode = (typeof AUTOMATION_MODE)[keyof typeof AUTOMATION_MODE];

/** `DEFAULT` means no ERP; otherwise the target is a connector key (§3.12). */
export const ACCOUNTING_TARGET = {
  DEFAULT: 'DEFAULT',
} as const;

export type AccountingTarget = (typeof ACCOUNTING_TARGET)[keyof typeof ACCOUNTING_TARGET];

export const ACCOUNTING_MAPPING_TYPE = {
  CURRENCY: 'CURRENCY',
  CUSTOMER: 'CUSTOMER',
  LOCATION: 'LOCATION',
  PAYMENT_METHOD: 'PAYMENT_METHOD',
  SOURCE_TYPE: 'SOURCE_TYPE',
  TAX_RATE: 'TAX_RATE',
} as const;

export type AccountingMappingType = (typeof ACCOUNTING_MAPPING_TYPE)[keyof typeof ACCOUNTING_MAPPING_TYPE];

/** What an `accounting_sync_jobs` row sends to the ERP (§3.18). `ISSUE` is `ERP_ISSUES` only; `POST` is `ERP_PUSH`. */
export const ACCOUNTING_SYNC_OPERATION = {
  CREDIT: 'CREDIT',
  ENSURE_CUSTOMER: 'ENSURE_CUSTOMER',
  ISSUE: 'ISSUE',
  PAYMENT: 'PAYMENT',
  POST: 'POST',
  VOID: 'VOID',
} as const;

export type AccountingSyncOperation = (typeof ACCOUNTING_SYNC_OPERATION)[keyof typeof ACCOUNTING_SYNC_OPERATION];

/** `FAILED` is retrying; `DEAD` needs a person (§3.18). */
export const ACCOUNTING_SYNC_JOB_STATUS = {
  DEAD: 'DEAD',
  FAILED: 'FAILED',
  IN_PROGRESS: 'IN_PROGRESS',
  PENDING: 'PENDING',
  SUCCEEDED: 'SUCCEEDED',
} as const;

export type AccountingSyncJobStatus = (typeof ACCOUNTING_SYNC_JOB_STATUS)[keyof typeof ACCOUNTING_SYNC_JOB_STATUS];

export const SERIES_RESET_POLICY = {
  NEVER: 'NEVER',
  YEARLY: 'YEARLY',
} as const;

export type SeriesResetPolicy = (typeof SERIES_RESET_POLICY)[keyof typeof SERIES_RESET_POLICY];

export const IDEMPOTENCY_STATE = {
  COMPLETED: 'COMPLETED',
  IN_PROGRESS: 'IN_PROGRESS',
} as const;

export type IdempotencyState = (typeof IDEMPOTENCY_STATE)[keyof typeof IDEMPOTENCY_STATE];

/** Whether an invoice must carry a base-currency conversion before issue (§3.1). */
export const BASE_CURRENCY_FX_POLICY = {
  OPTIONAL: 'OPTIONAL',
  REQUIRED: 'REQUIRED',
} as const;

export type BaseCurrencyFxPolicy = (typeof BASE_CURRENCY_FX_POLICY)[keyof typeof BASE_CURRENCY_FX_POLICY];

/** Source of the payment status projection (§3.1). `MANUAL` is the only stored value in the MVP. */
export const PAYMENT_STATUS_SOURCE = {
  ERP: 'ERP',
  MANUAL: 'MANUAL',
  PAYMENT_PROVIDER: 'PAYMENT_PROVIDER',
} as const;

export type PaymentStatusSource = (typeof PAYMENT_STATUS_SOURCE)[keyof typeof PAYMENT_STATUS_SOURCE];

/** Kind of a header or line reference (§3.5). */
export const INVOICE_REFERENCE_KIND = {
  AGREEMENT: 'AGREEMENT',
  CONTRACT: 'CONTRACT',
  DISPUTE: 'DISPUTE',
  EXPENSE: 'EXPENSE',
  PO: 'PO',
  /** The request reference of a contract fee re-bill, e.g. `SD/2026/000002` (SWOOP-416). */
  REQUEST: 'REQUEST',
  TICKET: 'TICKET',
  WORK_ORDER: 'WORK_ORDER',
} as const;

export type InvoiceReferenceKind = (typeof INVOICE_REFERENCE_KIND)[keyof typeof INVOICE_REFERENCE_KIND];

/** `ERP_ISSUES` draft lock (§3.5). */
export const EXTERNAL_ISSUE_STATUS = {
  CONFIRMED: 'CONFIRMED',
  REJECTED: 'REJECTED',
  REQUESTED: 'REQUESTED',
} as const;

export type ExternalIssueStatus = (typeof EXTERNAL_ISSUE_STATUS)[keyof typeof EXTERNAL_ISSUE_STATUS];

/** Optional grouping keys (§3.1). The mandatory keys cannot be removed and are not listed. */
export const GROUPING_KEY = {
  AGREEMENT: 'AGREEMENT',
  CONTRACT: 'CONTRACT',
  EVENT: 'EVENT',
  INVOICING_CYCLE: 'INVOICING_CYCLE',
  LOCATION: 'LOCATION',
  PO: 'PO',
} as const;

export type GroupingKey = (typeof GROUPING_KEY)[keyof typeof GROUPING_KEY];

export const EXPORT_CADENCE = {
  DAILY: 'DAILY',
  MONTHLY: 'MONTHLY',
  WEEKLY: 'WEEKLY',
} as const;

export type ExportCadence = (typeof EXPORT_CADENCE)[keyof typeof EXPORT_CADENCE];

export const EXPORT_FORMAT = {
  CSV: 'CSV',
  XLSX: 'XLSX',
} as const;

export type ExportFormat = (typeof EXPORT_FORMAT)[keyof typeof EXPORT_FORMAT];

export const EXPORT_DELIVERY = {
  ATTACHMENT: 'ATTACHMENT',
  LINK: 'LINK',
} as const;

export type ExportDelivery = (typeof EXPORT_DELIVERY)[keyof typeof EXPORT_DELIVERY];
